// frontend/src/components/librarian/LibrarianTopbar.tsx
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Search, Bell, Command } from 'lucide-react';
import { format } from 'date-fns';
import { useLibrarianTopbarNotifications } from '@/hooks/librarian-topbar-notifications';
import { useAuth } from '@/hooks/useAuth';
import { useRealTimeDashboard } from '@/hooks/use-real-time-dashboard';
import { useLibrarianTopbar } from '@/contexts/LibrarianTopbarContext';

const FALLBACK_TITLES: Record<string, string> = {
  '/librarian': 'Dashboard',
  '/librarian/books': 'Books',
  '/librarian/authors': 'Authors',
  '/librarian/publishers': 'Publishers',
  '/librarian/loans': 'Loans',
  '/librarian/members': 'Members',
  '/librarian/penalties': 'Penalties',
  '/librarian/tasks': 'Tasks',
  '/librarian/analytics': 'Analytics',
  '/librarian/notifications': 'Notifications',
  '/librarian/settings': 'Settings',
};

const fallbackTitleFor = (pathname: string): string => {
  if (FALLBACK_TITLES[pathname]) return FALLBACK_TITLES[pathname];
  const match = Object.keys(FALLBACK_TITLES).find(k => k !== '/librarian' && pathname.startsWith(k + '/'));
  return match ? FALLBACK_TITLES[match] : 'Librarian';
};

const LibrarianTopbar: React.FC = () => {
  const { title, subtitle } = useLibrarianTopbar();
  const { isConnected } = useRealTimeDashboard();
  const { unreadCount } = useLibrarianTopbarNotifications({ socketConnected: isConnected });
  const { user } = useAuth();
  const location = useLocation();

  const displayTitle = title && title !== 'Dashboard' ? title : fallbackTitleFor(location.pathname);
  const displaySubtitle = subtitle ?? (location.pathname === '/librarian'
    ? format(new Date(), "EEEE, MMMM d")
    : null);

  const initials = user?.first_name && user?.last_name
    ? `${user.first_name[0]}${user.last_name[0]}`
    : (user?.username?.substring(0, 2) ?? 'LB').toUpperCase();

  return (
    <header className="h-14 bg-[var(--lib-surface)] border-b border-[var(--lib-hairline)] flex items-center px-6 gap-4 flex-shrink-0">
      {/* Title + italic subtitle */}
      <div className="min-w-0">
        <h1 className="font-display text-[15px] font-semibold text-[var(--lib-ink)] leading-tight truncate">
          {displayTitle}
        </h1>
        {displaySubtitle && (
          <p className="font-serif italic text-[13px] text-[var(--lib-ink-muted)] leading-tight truncate">
            {displaySubtitle}
          </p>
        )}
      </div>

      <div className="flex-1" />

      {/* Search → opens CommandPalette */}
      <button
        className="h-8 flex items-center gap-2 px-3 w-[220px] border border-[var(--lib-hairline)] rounded-md text-[12px] text-[var(--lib-ink-muted)] hover:border-[var(--lib-gold)] transition-colors"
        onClick={() => window.dispatchEvent(new CustomEvent('kg:palette:open'))}
        title="Search or jump to… (⌘K)"
      >
        <Search className="h-3.5 w-3.5" />
        <span className="flex-1 text-left">Search or jump to…</span>
        <span className="inline-flex items-center gap-0.5 font-mono text-[11px]">
          <Command className="h-3 w-3" />K
        </span>
      </button>

      {/* Live/offline pulse */}
      <div className="flex items-center gap-1.5" title={isConnected ? 'Live' : 'Offline'}>
        <span className={`h-1.5 w-1.5 rounded-full ${isConnected ? 'bg-[var(--lib-gold)] animate-pulse' : 'bg-[var(--lib-ink-muted)]'}`} />
        <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)] hidden md:inline">
          {isConnected ? 'Live' : 'Offline'}
        </span>
      </div>

      {/* Bell */}
      <Link
        to="/librarian/notifications"
        className="relative w-8 h-8 rounded-md flex items-center justify-center text-[var(--lib-ink-muted)] hover:bg-[var(--lib-surface-2)] hover:text-[var(--lib-ink)] transition-colors"
        title={unreadCount > 0 ? `${unreadCount} unread` : 'Notifications'}
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span className="absolute top-1.5 right-1.5 min-w-[14px] h-[14px] px-1 rounded-full bg-[var(--lib-gold)] text-[var(--lib-on-gold)] text-[9px] font-bold flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Link>

      {/* Avatar */}
      <div
        className="w-8 h-8 rounded-full bg-[var(--lib-navy)] text-[var(--lib-sidebar)] flex items-center justify-center font-display font-semibold text-[11px]"
        title={user?.username}
      >
        {initials}
      </div>
    </header>
  );
};

export default LibrarianTopbar;
